function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
//  Author    : Tobias Schwarzkopf, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 161511 $
//  Purpose   : date handling menu components
// =============================================================================
// ---Third-party imports
import React, { Component, Fragment } from 'react';
import { Card, Button, Menu, Select, Input, Popup, Dropdown, Label } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { clone, map, dict, callIfFunc, filterItems, range, flatten, sorted, uniq } from 'ldash';
import connect from 'uix/connect';
import log from 'uix/logging';
import { VIcon } from 'uix/graphics';
import { addDays, subDays, subWeeks, subMonths, subYears, lastDayOfWeek as _lastDayOfWeek, lastDayOfMonth, lastDayOfYear, startOfDay, startOfWeek as _startOfWeek, startOfMonth, startOfYear, formatISO, differenceInDays, parseISO, formatDate } from 'uix/utils/date';
import { useResettableState, usePersistent, useRouter, useSelector } from 'uix/hooks';
import { isoToday, isoDate, GranularityStrategy } from 'uix/utils/date';
// ---Local imports
import { XPopup } from './popup';
import styles from './datepicker.css';
import { DebugFeature } from './debug';
// ---Globals
// prefer monday (instead of sunday)
const startOfWeek = (d)=>_startOfWeek(d, {
        weekStartsOn: 1
    });
const lastDayOfWeek = (d)=>_lastDayOfWeek(d, {
        weekStartsOn: 1
    });
// each child list is a new row
const QUICK_SELECT = {
    D: [
        [
            'D30',
            'D60',
            'D90'
        ],
        [
            'fullW1',
            'fullW2',
            'fullM1'
        ],
        [
            'fullM3',
            'fullY1',
            'Y'
        ]
    ],
    W: [
        [
            'Y',
            'fullM12',
            'M24'
        ],
        [
            'fullY2',
            'fullY3'
        ]
    ],
    M: [
        [
            'Y',
            'fullM12',
            'M24'
        ],
        [
            'fullY2',
            'fullY5',
            null
        ]
    ],
    Q: [
        [
            'Y10',
            null
        ]
    ],
    Y: [
        [
            'Y10',
            null
        ]
    ]
};
const lastDayOfFuncs = {
    W: lastDayOfWeek,
    M: lastDayOfMonth,
    Y: lastDayOfYear
};
const subFuncs = {
    D: subDays,
    W: subWeeks,
    M: subMonths,
    Y: subYears
};
const startOfFuncs = {
    D: startOfDay,
    W: startOfWeek,
    M: startOfMonth,
    Y: startOfYear
};
let decode = (interval)=>{
    /* decode 3 formats:
  plain - D, W, M, Y: from first day of interval until today
  delta - D30, W8, M6: today minus N times interval
  full - fullM2, fullY5: start of current interval minus N times interval
  */ let startDate = null;
    let endDate = new Date();
    if (interval === null) {
        return {
            startDate,
            endDate,
            decoded: {
                mode: null
            }
        };
    }
    if (startOfFuncs[interval]) {
        // simple interval is "this INTERVALL so far" mode
        startDate = startOfFuncs[interval](endDate);
        return {
            startDate,
            endDate,
            decoded: {
                mode: interval
            }
        };
    } else {
        let full = false;
        if (interval.startsWith('full')) {
            interval = interval.slice(4);
            full = true;
        }
        let mode = interval[0];
        let i = parseInt(interval.slice(1));
        let subFunc = subFuncs[mode];
        if (full) {
            let lastDayOfFunc = lastDayOfFuncs[mode];
            if (lastDayOfFunc) {
                endDate = lastDayOfFunc(subFunc(endDate, 1));
            }
        }
        startDate = subFunc(addDays(endDate, 1), i);
        return {
            startDate,
            endDate,
            decoded: {
                mode,
                repeat: i,
                full
            }
        };
    }
};
// ---Exports
export const DayPicker = ({ defaultValue = 30, onChange })=>{
    let { request, go } = useRouter();
    let [changedDays, setChangedDays] = useResettableState(null, [
        defaultValue
    ]);
    let i18n = useSelector(Sel.i18n);
    defaultValue = parseInt(defaultValue);
    defaultValue = defaultValue >= 0 ? defaultValue : 30;
    let days = changedDays ?? parseInt(request.days);
    days = days >= 0 ? days : defaultValue;
    let labels = (days)=>parseInt(days) !== 0 ? String(days) : `${i18n.get('all')}`;
    let _onChange = (days)=>{
        if (onChange) {
            onChange(days);
        } else {
            go({
                updateQuery: {
                    days
                }
            });
        }
        setChangedDays(days);
    };
    let dayList = sorted([
        3,
        7,
        30,
        90,
        365,
        defaultValue
    ]);
    dayList = uniq([
        ...dayList,
        0
    ]);
    let dayItems = dayList.map((e)=>/*#__PURE__*/ _jsx(Dropdown.Item, {
            content: labels(e),
            active: days == e,
            value: String(e),
            onClick: ()=>_onChange(days === e ? null : e)
        }, e));
    return /*#__PURE__*/ _jsx(Dropdown, {
        item: true,
        trigger: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(VIcon, {
                    name: "calendar alternate outline"
                }),
                `${labels(days)} ${i18n.get('days')}`
            ]
        }),
        children: /*#__PURE__*/ _jsx(Dropdown.Menu, {
            children: dayItems
        })
    });
};
class _DatePicker extends Component {
    static deriveMinDate(granularity, minDate, maxDate) {
        minDate = GranularityStrategy[granularity] ? Math.max(new Date(minDate), subDays(new Date(maxDate), GranularityStrategy[granularity])) : minDate;
        return formatDate(new Date(minDate), 'ISO_DATE');
    }
    static getDerivedStateFromProps(nextProps, prevState) {
        let { startDate, endDate, granularity, minDate, maxDate } = nextProps;
        const nextState = {};
        if (startDate !== prevState.propStartDate) {
            nextState.propStartDate = startDate;
            nextState.startDate = startDate || isoToday();
        }
        if (endDate !== prevState.propEndDate) {
            nextState.propEndDate = endDate;
            nextState.endDate = endDate || isoToday();
        }
        if (granularity !== prevState.propGranularity) {
            nextState.propGranularity = granularity;
            nextState.granularity = granularity;
        }
        maxDate = maxDate || isoToday();
        if (maxDate !== prevState.maxDate) {
            nextState.maxDate = maxDate;
        }
        if (minDate !== prevState.propMinDate) {
            nextState.propMinDate = minDate;
            nextState.minDate = _DatePicker.deriveMinDate(granularity, minDate, maxDate);
        }
        return nextState;
    }
    render() {
        const { onChange, showQuickSelection, className } = this.props;
        let { startDate, endDate, granularity, openDatepicker } = this.state;
        let isAvailable = (g)=>!GranularityStrategy[g] || GranularityStrategy[g] >= differenceInDays(new Date(endDate), new Date(startDate));
        let gInput = (g)=>/*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(Input, {
                        type: "radio",
                        id: `granularity_${g}`,
                        name: "granularity",
                        disabled: !isAvailable(g),
                        value: g,
                        checked: granularity === g,
                        onChange: (e)=>this.setGranularity(e.target.value)
                    }),
                    /*#__PURE__*/ _jsx("label", {
                        htmlFor: `granularity_${g}`,
                        children: this.translateGranularity(g)
                    })
                ]
            }, g);
        let inputs = this.availableGranularities().map(gInput);
        return /*#__PURE__*/ _jsx(XPopup, {
            fitted: true,
            basic: true,
            open: openDatepicker,
            onOpen: ()=>{
                this.setState({
                    openDatepicker: true
                });
            },
            onClose: ()=>{
                this.setState({
                    openDatepicker: false
                });
            },
            on: "click",
            className: clsx(styles.datePicker, className),
            trigger: /*#__PURE__*/ _jsxs(Menu.Item, {
                active: openDatepicker,
                role: "label",
                children: [
                    /*#__PURE__*/ _jsx(VIcon, {
                        name: "calendar alternate outline",
                        style: {
                            color: openDatepicker ? 'var(--vl_accent_color)' : 'grey'
                        }
                    }),
                    `${formatDate(new Date(startDate), 'P')}  -  ${formatDate(new Date(endDate), 'P')}`
                ]
            }),
            children: /*#__PURE__*/ _jsxs(Card, {
                className: styles.content,
                children: [
                    /*#__PURE__*/ _jsx(Card.Content, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: styles.dateSelection,
                            children: [
                                this.buildPicker('start'),
                                this.buildPicker('end')
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Card.Content, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: styles.granularity,
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    children: "Granularität:"
                                }),
                                inputs
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Card.Content, {
                        children: showQuickSelection && this.getQuickSelection()
                    }),
                    /*#__PURE__*/ _jsxs(Card.Content, {
                        children: [
                            /*#__PURE__*/ _jsx(DebugFeature, {
                                children: /*#__PURE__*/ _jsxs("span", {
                                    style: {
                                        fontSize: '0.8rem'
                                    },
                                    children: [
                                        this.state.minDate,
                                        " < dt < ",
                                        this.state.maxDate,
                                        ' | ',
                                        startDate,
                                        " - ",
                                        endDate
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                basic: true,
                                size: "small",
                                className: styles.submit,
                                onClick: ()=>{
                                    callIfFunc(onChange, {
                                        startDate,
                                        endDate,
                                        granularity
                                    });
                                    this.setState({
                                        openDatepicker: false
                                    });
                                },
                                children: "Übernehmen"
                            })
                        ]
                    })
                ]
            })
        });
    }
    constructor(props){
        super(props), _define_property(this, "createDate", (y, m, d)=>formatDate(new Date(y, parseInt(m) - 1, d), 'ISO_DATE')), _define_property(this, "setDate", ({ type, y, m, d })=>{
            let { startDate, endDate, granularity, minDate, maxDate } = this.state;
            let selectedDate = this.createDate(y, m, d);
            if (!selectedDate) {
                log.warn('cannot decode date %o-%o-%o', y, m, d);
                return;
            }
            let startTs = null;
            let endTs = null;
            if (type === 'start') {
                startTs = new Date(selectedDate).getTime();
                // although we limit min/max on <input>, we can still invalidate boundaries with cursor up/down
                if (startTs < new Date(minDate).getTime()) {
                    selectedDate = formatDate(new Date(minDate), 'ISO_DATE');
                }
                endTs = new Date(endDate).getTime();
                startDate = selectedDate;
            } else {
                startTs = new Date(startDate).getTime();
                endTs = new Date(selectedDate).getTime();
                // although we limit min/max on <input>, we can still invalidate boundaries with cursor up/down
                if (endTs > new Date(maxDate).getTime()) {
                    selectedDate = formatDate(new Date(maxDate), 'ISO_DATE');
                }
                endDate = selectedDate;
            }
            // check constraints
            if (startTs >= endTs) {
                if (granularity === 'Y') {
                    if (type === 'start') {
                        endDate = this.createDate(y, 12, 31);
                    } else {
                        startDate = this.createDate(y, 1, 1);
                    }
                } else if (granularity === 'Q') {
                    if (type === 'start') {
                        let [q_m, q_d] = this.quartMonthDay('end', y, Math.ceil(m / 3));
                        endDate = this.createDate(y, q_m, q_d);
                    } else {
                        let [q_m, q_d] = this.quartMonthDay('start', y, Math.ceil(m / 3));
                        startDate = this.createDate(y, q_m, q_d);
                    }
                } else if (granularity === 'M') {
                    if (type === 'start') {
                        endDate = this.createDate(y, m, this.lastDayOfMonth(y, m));
                    } else {
                        startDate = this.createDate(y, m, 1);
                    }
                } else if (granularity === 'W') {
                    //XXX: subtract 7 or 6?
                    if (type === 'start') {
                        endDate = formatDate(addDays(new Date(this.createDate(y, m, d)), 7), 'ISO_DATE');
                    } else {
                        startDate = formatDate(subDays(new Date(this.createDate(y, m, d)), 7), 'ISO_DATE');
                    }
                } else if (granularity === 'D') {
                    endDate = selectedDate;
                    startDate = selectedDate;
                }
            }
            this.checkGranularity(startDate, endDate);
            this.setState({
                startDate,
                endDate
            });
        }), _define_property(this, "checkGranularity", (startDate, endDate)=>{
            /* if day delta invalidates GranularityStrategy, reduce granularity
     */ let { granularity } = this.state;
            let limit = GranularityStrategy[granularity];
            let diffDays = differenceInDays(new Date(endDate), new Date(startDate));
            let available = this.availableGranularities();
            if (limit && diffDays > limit) {
                let [next_g] = filterItems(GranularityStrategy, (g, dayLimit)=>(!dayLimit || diffDays < dayLimit) && available.includes(g))[0];
                if (next_g) {
                    this.setState({
                        granularity: next_g
                    });
                } else {
                    log.warn('got no available granularity for diffDays %o', diffDays);
                }
            }
        }), _define_property(this, "lastDayOfMonth", (year, month)=>lastDayOfMonth(new Date(parseInt(year), parseInt(month), 0)).getDate()), _define_property(this, "getGranularDate", (d)=>{
            /* input iso-date format
     */ let { granularity } = this.state;
            if (d) {
                if (granularity == 'W') d = formatDate(new Date(d), "yyyy-'W'ww");
                if (granularity == 'M') d = formatDate(new Date(d), 'yyyy-LL');
                if (granularity == 'Y') d = d.split('-')[0];
            }
            return d;
        }), _define_property(this, "quartMonthDay", (type, y, q)=>{
            if (type == 'start') {
                let m = (q - 1) * 3 + 1;
                return [
                    m,
                    1
                ];
            } else {
                let m = (q - 1) * 3 + 3;
                return [
                    m,
                    lastDayOfMonth(new Date(y, m - 1, 15)).getDate()
                ];
            }
        }), _define_property(this, "buildPicker", (type)=>{
            let { startDate, endDate, granularity, minDate, maxDate } = this.state;
            let typeDate = type === 'start' ? startDate : endDate;
            let [y, m, d] = typeDate.split('-');
            let changeYear = (value)=>this.setDate({
                    type,
                    y: value,
                    m: type === 'start' ? 1 : 12,
                    d: type === 'start' ? 1 : 31
                });
            let changeQuart = (value)=>{
                let [y, q] = value.split('-');
                q = parseInt(q);
                let [m, d] = this.quartMonthDay(type, y, q);
                //~ let m = type === 'start' ? (q - 1) * 3 + 1 : (q - 1) * 3 + 3
                this.setDate({
                    type,
                    y,
                    m,
                    d
                });
            };
            let changeMonth = (value)=>{
                const [y, m] = value.split('-');
                this.setDate({
                    type,
                    y,
                    m,
                    d: type === 'start' ? 1 : lastDayOfMonth(new Date(value)).getDate()
                });
            };
            let changeWeek = (value)=>{
                let d = parseISO(value);
                if (type === 'end') {
                    d = lastDayOfWeek(d);
                }
                this.setDate({
                    type,
                    y: d.getFullYear(),
                    m: d.getMonth() + 1,
                    d: d.getDate()
                });
            };
            let changeDay = (value)=>{
                const [y, m, d] = value.split('-');
                this.setDate({
                    type,
                    y,
                    m,
                    d
                });
            };
            let dateInput = (itype, setter)=>/*#__PURE__*/ _jsx(Input, {
                    className: styles.dateInput,
                    onChange: (e)=>e.target.value && setter(e.target.value),
                    value: this.getGranularDate(typeDate),
                    type: itype,
                    min: this.getGranularDate(minDate),
                    max: this.getGranularDate(maxDate)
                });
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    granularity === 'Y' && /*#__PURE__*/ _jsx(Select, {
                        className: styles.dateSelect,
                        options: range(parseInt(minDate.split('-')[0]), parseInt(maxDate.split('-')[0]) + 1).map((y)=>({
                                text: String(y),
                                value: String(y)
                            })),
                        value: y,
                        onChange: (e, d)=>changeYear(d.value)
                    }),
                    granularity === 'Q' && /*#__PURE__*/ _jsx(Select, {
                        className: styles.dateSelect,
                        options: flatten(range(parseInt(minDate.split('-')[0]), parseInt(maxDate.split('-')[0]) + 1).map((y)=>[
                                1,
                                2,
                                3,
                                4
                            ].map((q)=>({
                                    text: `${y} Q.${q}`,
                                    value: `${y}-${q}`
                                })))),
                        value: `${y}-${Math.ceil(m / 3)}`,
                        onChange: (e, d)=>changeQuart(d.value)
                    }),
                    granularity === 'M' && dateInput('month', changeMonth),
                    granularity === 'W' && dateInput('week', changeWeek),
                    granularity === 'D' && dateInput('date', changeDay)
                ]
            });
        }), _define_property(this, "setGranularity", (g)=>{
            const { startDate, endDate, maxDate } = this.state;
            const [startY, startM = 1] = startDate.split('-');
            const [endY, endM = 12] = endDate.split('-');
            let minDate = _DatePicker.deriveMinDate(g, this.props.minDate, maxDate);
            let nextState = {
                granularity: g,
                minDate
            };
            if (g === 'Y') {
                nextState = {
                    ...nextState,
                    startDate: this.createDate(startY, 1, 1),
                    endDate: this.createDate(endY, 12, 31)
                };
            } else if (g === 'M') {
                nextState = {
                    ...nextState,
                    startDate: this.createDate(startY, startM, 1),
                    endDate: this.createDate(endY, endM, this.lastDayOfMonth(endY, endM))
                };
            }
            this.setState(nextState);
        }), _define_property(this, "getQuickSelection", ()=>{
            let { minDate, maxDate, granularity } = this.state;
            let quicks = QUICK_SELECT[granularity];
            if (!quicks) {
                return null;
            }
            let createQuickSelectionItem = (interval, key)=>{
                let { startDate, endDate, decoded } = decode(interval);
                let label = 'n/a';
                if (decoded.mode === null) {
                    startDate = minDate;
                    endDate = maxDate;
                    label = 'Alle Jahre';
                } else {
                    startDate = isoDate(startDate);
                    endDate = isoDate(endDate);
                    let s_this = {
                        D: 'Dieser',
                        W: 'Diese',
                        M: 'Dieser',
                        Y: 'Dieses'
                    }[decoded.mode];
                    let s_last = {
                        D: 'Letzter',
                        W: 'Letzte',
                        M: 'Letzter',
                        Y: 'Letztes'
                    }[decoded.mode];
                    let s = {
                        D: 'Tag',
                        W: 'Woche',
                        M: 'Monat',
                        Y: 'Jahr'
                    }[decoded.mode];
                    let suffix = {
                        D: 'e',
                        W: 'n',
                        M: 'e',
                        Y: 'e'
                    }[decoded.mode];
                    if (decoded.repeat === undefined) {
                        label = `${s_this} ${s}`;
                    } else if (decoded.full) {
                        if (decoded.repeat > 1) {
                            label = `Letzte ${decoded.repeat} ${s}${suffix}`;
                        } else {
                            label = `${s_last} ${s}`;
                        }
                    } else {
                        label = `Minus ${decoded.repeat} ${s}e`;
                    }
                }
                let onClick = ()=>this.onQuickSelect(startDate, endDate, granularity);
                return /*#__PURE__*/ _jsx(Button, {
                    basic: true,
                    compact: true,
                    color: "grey",
                    onClick: onClick,
                    children: label
                }, key);
            };
            return /*#__PURE__*/ _jsx("div", {
                className: styles.quickSelection,
                children: quicks.map((row, i)=>/*#__PURE__*/ _jsx("div", {
                        className: styles.row,
                        children: row.map((item, i)=>createQuickSelectionItem(item, i))
                    }, i))
            });
        }), _define_property(this, "onQuickSelect", (startDate, endDate, granularity)=>{
            const { submitOnQuickSelect, go, onChange } = this.props;
            if (submitOnQuickSelect) {
                go({
                    updateQuery: {
                        rule: granularity,
                        startDate,
                        endDate
                    }
                });
            } else {
                this.setState({
                    startDate: formatDate(startDate, 'ISO_DATE'),
                    endDate: formatDate(endDate, 'ISO_DATE'),
                    granularity
                });
            }
            callIfFunc(onChange, {
                startDate,
                endDate,
                granularity
            });
            this.setState({
                openDatepicker: false
            });
        }), _define_property(this, "translateGranularity", (g)=>{
            let { i18n } = this.props;
            let s = {
                Y: 'year',
                Q: 'quart',
                M: 'month',
                W: 'week',
                D: 'day'
            }[g];
            return i18n.get(s);
        }), _define_property(this, "availableGranularities", ()=>[
                'years',
                'quarts',
                'months',
                'weeks',
                'days'
            ].filter((g)=>this.props[g]).map((g)=>g[0].toUpperCase()));
        let { className } = props;
        this.state = {
            openDatepicker: false,
            startDate: isoToday(),
            endDate: isoToday(),
            granularity: ([
                'years',
                'quart',
                'months',
                'weeks',
                'days'
            ].filter((g)=>this.props[g])[0] || 'd')[0].toUpperCase(),
            ..._DatePicker.getDerivedStateFromProps(props, {})
        };
    }
}
_define_property(_DatePicker, "defaultProps", {
    open: false,
    toggle: null,
    className: null,
    showQuickSelection: true,
    submitOnQuickSelect: true,
    granularity: undefined,
    minDate: '2010',
    // active granularities
    years: false,
    quarts: false,
    months: false,
    weeks: false,
    days: true
});
_define_property(_DatePicker, "storeMap", (root)=>({
        i18n: Sel.i18n(root)
    }));
_define_property(_DatePicker, "actionMap", {
    go: Actions.go
});
export const DatePicker = connect('DatePicker', _DatePicker);
